export type PostSection = {
  heading: string;
  paragraphs: string[];
};

export type Post = {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  tags: string[];
  author: string;
  date: string;
  dateLabel: string;
  readTime: string;
  heat: number;
  comments: number;
  image: string;
  imageAlt: string;
  featured?: boolean;
  sections: PostSection[];
  keyPoints: string[];
};

export type Category = {
  name: string;
  slug: string;
  desc: string;
  icon: string;
  count: number;
};

export const categories: Category[] = [
  { name: "明星动态", slug: "star", desc: "公开行程、采访与行业动向", icon: "ri-user-star-line", count: 128 },
  { name: "综艺爆料", slug: "variety", desc: "节目看点、嘉宾阵容与录制现场", icon: "ri-mic-line", count: 96 },
  { name: "剧集热闻", slug: "drama", desc: "定档消息、题材趋势与拍摄观察", icon: "ri-film-line", count: 154 },
  { name: "演出前线", slug: "live", desc: "音乐节、演唱会与现场回顾", icon: "ri-music-2-line", count: 73 },
  { name: "网络热梗", slug: "meme", desc: "平台热榜、挑战玩法与流行语", icon: "ri-fire-line", count: 112 },
  { name: "品牌风波", slug: "brand", desc: "联名、代言与消费话题", icon: "ri-shopping-bag-3-line", count: 61 },
];

export const posts: Post[] = [
  {
    id: "p1",
    title: "新剧定档盘点：多部待播剧进入排播期，古装与都市题材正面交锋",
    excerpt:
      "进入九月，多部此前长期压箱底的剧集陆续释放定档信号。古装与都市两条赛道的排播节奏明显加快，观众最关心的还是成片质感与故事节奏。",
    category: "剧集热闻",
    categorySlug: "drama",
    tags: ["定档", "待播剧", "排播"],
    author: "瓜田观察员",
    date: "2026-09-10",
    dateLabel: "09-10 09:20",
    readTime: "6 分钟",
    heat: 9821,
    comments: 326,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20cinematic%20film%20set%20with%20warm%20amber%20lighting%2C%20vintage%20projector%20and%20film%20reels%20on%20a%20wooden%20table%2C%20soft%20cream%20background%2C%20editorial%20magazine%20photography%2C%20warm%20orange%20and%20cream%20palette%2C%20artistic%20minimal%20composition&width=800&height=600&seq=hg-p1-drama&orientation=landscape",
    imageAlt: "暖色调电影胶片与放映机摆件，寓意新剧定档盘点",
    featured: true,
    sections: [
      {
        heading: "排播节奏为什么突然密集",
        paragraphs: [
          "每年第三季度末到第四季度初，都是剧集排播的传统密集期。一方面平台希望借节假日的观看高峰完成年度内容沉淀，另一方面制作方也希望赶在评奖与招商周期前让作品与观众见面。",
          "因此这段时间出现「一周多部定档」的情况并不意外。真正值得关注的是题材分布：古装与都市依然是主力，但短剧集与单元剧的比例在悄悄上升。",
        ],
      },
      {
        heading: "古装与都市各自的机会在哪",
        paragraphs: [
          "古装剧的优势是世界观完整、视觉记忆点强，只要服化道过关，很容易在社交平台形成讨论；短板则是前几集的节奏如果拖沓，很容易劝退新观众。",
          "都市剧正好相反，题材贴近生活、代入快，但对剧本的真实感要求更高。一旦人物动机站不住脚，评论区就会迅速反噬。",
        ],
      },
      {
        heading: "观众到底在期待什么",
        paragraphs: [
          "从近期的讨论热度看，观众最在意的其实不是阵容有多豪华，而是故事能不能自洽、人物有没有成长。宣传物料可以吸引第一波点击，但留存靠的始终是内容本身。",
        ],
      },
    ],
    keyPoints: [
      "九月进入剧集排播密集期，古装与都市题材集中释放",
      "短剧集与单元剧比例上升，成片质感成为讨论焦点",
      "观众更关注故事自洽度，而非单纯的阵容话题",
    ],
  },
  {
    id: "p2",
    title: "综艺现场互动引热议：节目效果与真情实感的边界在哪",
    excerpt:
      "最新一期节目播出后，一段现场互动片段迅速登上话题榜。有人认为这是自然流露，也有人觉得更像精心设计的桥段，讨论热度居高不下。",
    category: "综艺爆料",
    categorySlug: "variety",
    tags: ["综艺", "录制现场", "话题"],
    author: "前排小板凳",
    date: "2026-09-10",
    dateLabel: "09-10 08:05",
    readTime: "5 分钟",
    heat: 9120,
    comments: 288,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20television%20studio%20stage%20with%20warm%20amber%20spotlights%2C%20empty%20director%20chairs%20and%20microphone%20stands%2C%20cream%20colored%20backdrop%2C%20editorial%20magazine%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20clean%20minimal%20composition&width=800&height=600&seq=hg-p2-variety&orientation=landscape",
    imageAlt: "暖色聚光灯下的综艺录制舞台与话筒架",
    featured: true,
    sections: [
      {
        heading: "一段互动为什么能冲上热搜",
        paragraphs: [
          "片段本身并不复杂，但剪辑节奏、配乐以及机位的选择，都会放大某个瞬间的情绪。观众在短短几十秒里接收到的信息，往往比完整流程更强烈。",
          "这也是综艺叙事的特点：把真实的反应放进设计好的结构里，让情绪在那个点上爆发。",
        ],
      },
      {
        heading: "节目效果与真实之间的分寸",
        paragraphs: [
          "成熟的综艺团队通常会给出情境与规则，但把反应留给嘉宾自己。这样既有看点，又不会显得刻意。",
          "观众其实很擅长分辨「演」和「真」。一旦某个环节被看穿是为了镜头而设计，反噬来得也很快。",
        ],
      },
    ],
    keyPoints: [
      "剪辑节奏与配乐放大了现场互动的情绪浓度",
      "成熟团队提供情境、保留真实反应是常见做法",
      "观众对刻意设计的桥段辨识度越来越高",
    ],
  },
  {
    id: "p3",
    title: "音乐节首日回顾：现场观众热情不减，多组乐队带来惊喜返场",
    excerpt:
      "为期三天的音乐节拉开帷幕，首日现场人气超出主办方预期。多组乐队在固定曲目之外临时加演，成为当晚最被讨论的瞬间。",
    category: "演出前线",
    categorySlug: "live",
    tags: ["音乐节", "现场", "返场"],
    author: "现场第一排",
    date: "2026-09-09",
    dateLabel: "09-09 22:40",
    readTime: "5 分钟",
    heat: 8340,
    comments: 212,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20outdoor%20music%20festival%20stage%20at%20dusk%20with%20warm%20amber%20stage%20lights%20and%20crowd%20silhouettes%2C%20cream%20toned%20sky%2C%20editorial%20photography%2C%20warm%20orange%20and%20sand%20palette%2C%20artistic%20wide%20composition&width=800&height=600&seq=hg-p3-live&orientation=landscape",
    imageAlt: "黄昏时分暖色灯光下音乐节舞台与人群剪影",
    featured: false,
    sections: [
      {
        heading: "首日现场的几个变化",
        paragraphs: [
          "相比往年，今年入场流程的秩序感更好，分时段检票让人流压力被摊平。观众席的补水点与遮阳区也明显增加。",
          "舞台方面，音响调音比预期更稳，前排与后排的听感差距缩小，这直接影响了大合唱的现场效果。",
        ],
      },
      {
        heading: "为什么返场会成为话题",
        paragraphs: [
          "临时的加演往往是当天情绪的最高点。它不在节目单里，观众没有预期，所以冲击力更强，也更容易被记录下来传播。",
        ],
      },
    ],
    keyPoints: [
      "分时段检票让入场秩序与人流压力得到改善",
      "现场调音稳定，大合唱效果成为讨论重点",
      "临时加演因为不可预期，成为话题传播高点",
    ],
  },
  {
    id: "p4",
    title: "短视频平台热门挑战复盘：一支舞蹈为何能火遍全网",
    excerpt:
      "过去两周，一支节奏简单的舞蹈在各大平台反复出现。从素人到专业舞者都在翻跳，它到底踩中了什么样的传播规律？",
    category: "网络热梗",
    categorySlug: "meme",
    tags: ["挑战", "热梗", "传播"],
    author: "冲浪选手",
    date: "2026-09-09",
    dateLabel: "09-09 19:10",
    readTime: "4 分钟",
    heat: 7980,
    comments: 405,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20smartphone%20on%20a%20cream%20desk%20displaying%20colorful%20abstract%20shapes%2C%20warm%20amber%20desk%20lamp%20glow%2C%20editorial%20product%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20clean%20minimal%20composition&width=800&height=600&seq=hg-p4-meme&orientation=landscape",
    imageAlt: "暖光书桌上的手机与抽象彩色图形，象征短视频热门挑战",
    featured: false,
    sections: [
      {
        heading: "低门槛是传播的第一前提",
        paragraphs: [
          "这段舞蹈的动作拆解下来只有四组，任何人在半小时内都能学会。低门槛意味着更多人愿意尝试，而数量本身就是传播的燃料。",
          "同时它的配乐有清晰的鼓点，方便卡点，也让不同水平的参与者都能做出还算完整的效果。",
        ],
      },
      {
        heading: "翻跳里的二创空间",
        paragraphs: [
          "真正让热度持续的，是参与者加入的个人元素：有人换装、有人加剧情、有人做成对比视频。规则越简单，二创的余地反而越大。",
        ],
      },
    ],
    keyPoints: [
      "动作门槛低，任何人都能在短时间内完成翻跳",
      "配乐鼓点清晰，便于卡点与快速剪辑",
      "二创空间大，是热度持续的关键原因",
    ],
  },
  {
    id: "p5",
    title: "品牌联名再出圈：限定包装上线即售罄，二手价格水涨船高",
    excerpt:
      "又一波品牌联名上线，限定包装在开售当天迅速售罄。有人为了收藏，有人为了转手，围绕联名消费的讨论再次升温。",
    category: "品牌风波",
    categorySlug: "brand",
    tags: ["联名", "限定", "消费"],
    author: "钱包守门员",
    date: "2026-09-08",
    dateLabel: "09-08 15:30",
    readTime: "5 分钟",
    heat: 7210,
    comments: 176,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20limited%20edition%20product%20packaging%20boxes%20with%20warm%20amber%20ribbon%2C%20cream%20studio%20background%2C%20editorial%20product%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20soft%20shadows%2C%20clean%20minimal%20composition&width=800&height=600&seq=hg-p5-brand&orientation=landscape",
    imageAlt: "暖色丝带装饰的限定联名包装盒",
    featured: false,
    sections: [
      {
        heading: "限定为什么总能激起购买欲",
        paragraphs: [
          "「限定」本质上是在制造稀缺感。当数量与时间都被限制，决策成本会被压低，犹豫的空间也随之缩小。",
          "再加上社交平台的晒单氛围，拥有一件限定品从消费行为变成了表达行为。",
        ],
      },
      {
        heading: "理性看待二次溢价",
        paragraphs: [
          "售罄之后二手价格上浮是常见现象，但溢价能否维持，取决于后续是否补货以及真实的收藏需求有多大。跟风追高往往要承担较高风险。",
        ],
      },
    ],
    keyPoints: [
      "限定策略通过稀缺感压缩消费者的犹豫时间",
      "社交晒单让联名消费带有表达属性",
      "二次溢价取决于补货情况与真实需求",
    ],
  },
  {
    id: "p6",
    title: "幕后花絮：一部剧的服化道要经过多少次返工",
    excerpt:
      "镜头里几秒钟的一个场景，背后可能是数周的推翻重做。我们梳理了服化道团队从定稿到实拍的真实流程。",
    category: "剧集热闻",
    categorySlug: "drama",
    tags: ["幕后", "服化道", "制作"],
    author: "幕后小剧务",
    date: "2026-09-08",
    dateLabel: "09-08 11:15",
    readTime: "7 分钟",
    heat: 6640,
    comments: 143,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20costume%20design%20studio%20with%20warm%20amber%20lighting%2C%20fabric%20swatches%20and%20sketches%20on%20a%20cream%20table%2C%20editorial%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20artistic%20detail%20composition&width=800&height=600&seq=hg-p6-costume&orientation=landscape",
    imageAlt: "暖光服装设计工作室中的布料样本与设计手稿",
    featured: false,
    sections: [
      {
        heading: "从文字到成衣的距离",
        paragraphs: [
          "剧本里一句「身着素色长衫」，到了服化道团队手里会被拆成时代背景、人物身份、场合与演员身形四个维度。任何一个维度判断不同，成衣都要重做。",
          "这也是为什么很多剧组会在开机前进行多轮试装，宁可前期多花时间，也不愿现场临时调整。",
        ],
      },
      {
        heading: "返工的成本在哪里",
        paragraphs: [
          "返工不只是布料成本，还有工期与人力的连锁反应。一套主要角色的服装可能要同时准备多套备用，以应对不同的机位与动作需求。",
          "因此成熟的制作流程会在定稿阶段反复确认，把不确定性尽量消灭在开拍之前。",
        ],
      },
    ],
    keyPoints: [
      "一句剧本描述要拆解为时代、身份、场合与身形四个维度",
      "开机前的多轮试装是为了降低现场返工率",
      "返工成本包含工期与人力，而非只有材料费用",
    ],
  },
  {
    id: "p7",
    title: "演员转型幕后观察：从台前到制作人的路径怎么走",
    excerpt:
      "近年来，越来越多演员开始出现在出品人与监制的名单里。这条转型路径到底是资源积累的必然，还是风险不小的尝试？",
    category: "明星动态",
    categorySlug: "star",
    tags: ["转型", "制作人", "行业"],
    author: "行业观察席",
    date: "2026-09-07",
    dateLabel: "09-07 17:45",
    readTime: "6 分钟",
    heat: 6120,
    comments: 129,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20film%20production%20desk%20with%20warm%20amber%20lamp%2C%20script%20pages%20and%20clapperboard%20on%20cream%20surface%2C%20editorial%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20clean%20minimal%20composition&width=800&height=600&seq=hg-p7-producer&orientation=landscape",
    imageAlt: "暖色台灯下的剧本与场记板，象征转型幕后的制作工作",
    featured: false,
    sections: [
      {
        heading: "台前经验带来的优势",
        paragraphs: [
          "长期在片场的演员对内容有着直观判断，他们更清楚一场戏在成片中是否成立，也更容易与创作团队沟通。",
          "此外，演员积累的行业人脉在组建班底时能明显提高效率，这是转型者最实在的起点。",
        ],
      },
      {
        heading: "需要补上的短板",
        paragraphs: [
          "制作人最终要对预算、周期与结果负责，这与表演的逻辑完全不同。缺少项目管理经验，很容易在成本控制上吃亏。",
          "所以更稳妥的路径通常是从监制或联合出品开始，逐步熟悉全流程，而不是一步到位。",
        ],
      },
    ],
    keyPoints: [
      "片场经验让转型者对内容判断更直观",
      "行业人脉是组建班底时最实在的优势",
      "预算与周期管理是必须补上的能力短板",
    ],
  },
  {
    id: "p8",
    title: "影视取景地打卡热：取景地经济如何被一部剧带火",
    excerpt:
      "剧集播出后，取景地往往会在短时间内迎来客流高峰。地方文旅与剧组之间，正在形成一种新的互动关系。",
    category: "剧集热闻",
    categorySlug: "drama",
    tags: ["取景地", "文旅", "打卡"],
    author: "在路上看剧",
    date: "2026-09-07",
    dateLabel: "09-07 10:20",
    readTime: "5 分钟",
    heat: 5760,
    comments: 118,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20scenic%20ancient%20town%20alley%20with%20warm%20amber%20sunset%20light%2C%20cream%20stone%20walls%20and%20lanterns%2C%20editorial%20travel%20photography%2C%20warm%20orange%20and%20sand%20palette%2C%20artistic%20composition&width=800&height=600&seq=hg-p8-location&orientation=landscape",
    imageAlt: "暖色夕阳下的古镇巷道取景地",
    featured: false,
    sections: [
      {
        heading: "热度从哪里来",
        paragraphs: [
          "剧集提供的是一个带有情绪的场景记忆。观众到访的不只是一条街，而是想重现某个片段里被放大的氛围。",
          "这种情绪驱动比单纯的风景宣传更有穿透力，也更容易在社交平台形成连锁传播。",
        ],
      },
      {
        heading: "热度之后如何留下人",
        paragraphs: [
          "短期客流容易来也容易走。真正能沉淀下来的，是把打卡点与本地餐饮、住宿、手作体验串联成完整线路。",
        ],
      },
    ],
    keyPoints: [
      "观众打卡的核心动机是重现剧集中的场景情绪",
      "情绪驱动比单纯风景宣传更具传播穿透力",
      "与本地消费串联才能让短期热度沉淀下来",
    ],
  },
  {
    id: "p9",
    title: "演唱会门票开售即秒空：实名制与防抢票措施再受关注",
    excerpt:
      "新一轮巡演开票再度出现秒空，票务公平成为讨论焦点。实名制、限购与退票规则，正在被反复检视。",
    category: "演出前线",
    categorySlug: "live",
    tags: ["门票", "实名制", "票务"],
    author: "抢票失败者",
    date: "2026-09-06",
    dateLabel: "09-06 20:00",
    readTime: "5 分钟",
    heat: 5380,
    comments: 264,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20concert%20tickets%20and%20wristbands%20on%20a%20cream%20desk%20with%20warm%20amber%20light%2C%20editorial%20product%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20soft%20shadows%2C%20clean%20minimal%20composition&width=800&height=600&seq=hg-p9-ticket&orientation=landscape",
    imageAlt: "暖光下的演唱会门票与手环",
    featured: false,
    sections: [
      {
        heading: "秒空背后的供需关系",
        paragraphs: [
          "热门巡演的场馆容量是固定的，而关注人数可能数十倍于座位数。供需失衡是秒空的根本原因，技术手段只能缓解而无法消除。",
        ],
      },
      {
        heading: "规则在往哪个方向走",
        paragraphs: [
          "实名制与强退票规则是目前被讨论最多的两项。前者抑制囤票，后者让临时无法到场的观众有机会把票退回官方渠道。",
          "两者的共同点是压缩中间倒卖空间，但执行细节仍需不断打磨。",
        ],
      },
    ],
    keyPoints: [
      "场馆容量固定与关注量级悬殊，是秒空的根本原因",
      "实名制用于抑制囤票与倒卖",
      "强退票规则让临时无法到场的票回流官方渠道",
    ],
  },
  {
    id: "p10",
    title: "网文改编潮观察：什么样的故事更容易被搬上屏幕",
    excerpt:
      "网络文学依然是影视改编的重要源头。从改编成功率看，题材、结构与人物弧光，往往比热度更关键。",
    category: "剧集热闻",
    categorySlug: "drama",
    tags: ["改编", "网文", "内容"],
    author: "剧本旁听生",
    date: "2026-09-06",
    dateLabel: "09-06 14:10",
    readTime: "6 分钟",
    heat: 4910,
    comments: 97,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20open%20books%20stacked%20on%20a%20cream%20desk%20with%20warm%20amber%20light%2C%20editorial%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20soft%20shadows%2C%20clean%20minimal%20composition&width=800&height=600&seq=hg-p10-adapt&orientation=landscape",
    imageAlt: "暖光下堆叠的书籍，象征网络文学改编",
    featured: false,
    sections: [
      {
        heading: "结构比热度更重要",
        paragraphs: [
          "改编最怕的是原作主线松散。热度再高，如果故事缺乏清晰的目标与冲突，落在剧本阶段就会变得难以推进。",
          "相对而言，主线明确、章节推进有节奏的作品，改编难度明显更低。",
        ],
      },
      {
        heading: "人物弧光是留存关键",
        paragraphs: [
          "观众记住的往往不是设定有多新奇，而是人物经历了什么变化。能让观众在意角色命运的作品，改编后更容易留住人。",
        ],
      },
    ],
    keyPoints: [
      "主线清晰、推进有节奏的作品改编难度更低",
      "人物弧光比设定的新奇程度更能留住观众",
      "原作热度并非改编成功的决定性因素",
    ],
  },
  {
    id: "p11",
    title: "综艺嘉宾阵容公布：跨圈层组合能否带来新看点",
    excerpt:
      "新一季节目公布了嘉宾名单，不同领域的参与者被放进同一个场景。跨界组合的化学反应，成为开播前最大的悬念。",
    category: "综艺爆料",
    categorySlug: "variety",
    tags: ["阵容", "跨界", "新一季"],
    author: "预告片十级学者",
    date: "2026-09-05",
    dateLabel: "09-05 18:25",
    readTime: "4 分钟",
    heat: 4620,
    comments: 156,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20row%20of%20empty%20wooden%20chairs%20on%20a%20cream%20stage%20with%20warm%20amber%20spotlights%2C%20editorial%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20clean%20minimal%20composition&width=800&height=600&seq=hg-p11-cast&orientation=landscape",
    imageAlt: "暖色聚光灯下的一排空椅子，象征综艺嘉宾阵容",
    featured: false,
    sections: [
      {
        heading: "跨界组合的价值",
        paragraphs: [
          "不同背景的参与者自带不同的沟通方式与知识结构，一旦被放进需要协作的任务中，差异就会变成看点。",
          "不过差异也意味着磨合成本，前期如果缺少有效的破冰设计，很容易出现各说各话的尴尬。",
        ],
      },
      {
        heading: "观众在期待什么",
        paragraphs: [
          "比起固定的名单，观众更在意节目能不能给出新鲜的任务机制。阵容是入口，机制才是留存的关键。",
        ],
      },
    ],
    keyPoints: [
      "跨界参与者的差异本身就是内容看点",
      "缺少破冰设计容易导致前期节奏割裂",
      "任务机制的创新程度决定长期留存",
    ],
  },
  {
    id: "p12",
    title: "红毯造型盘点：今年流行这些配色与面料",
    excerpt:
      "近期几场活动的红毯造型陆续释出，暖色系与哑光面料成为高频选择。我们从配色、剪裁与配饰三个角度看这一季的审美走向。",
    category: "明星动态",
    categorySlug: "star",
    tags: ["红毯", "造型", "时尚"],
    author: "造型显微镜",
    date: "2026-09-05",
    dateLabel: "09-05 12:00",
    readTime: "5 分钟",
    heat: 4290,
    comments: 88,
    image:
      "https://readdy.ai/api/search-image?query=Stylized%20couture%20fabric%20drapes%20on%20a%20cream%20studio%20backdrop%20with%20warm%20amber%20lighting%2C%20editorial%20fashion%20photography%2C%20warm%20orange%20and%20beige%20palette%2C%20soft%20shadows%2C%20elegant%20minimal%20composition&width=800&height=600&seq=hg-p12-redcarpet&orientation=landscape",
    imageAlt: "暖色灯光下米色背景前的礼服面料垂坠造型",
    featured: false,
    sections: [
      {
        heading: "配色：暖色回到主流",
        paragraphs: [
          "相比高饱和的撞色，今年的红毯更倾向于焦糖、砖红与米金这类暖色。它们在上镜时更耐看，也更不容易被灯光吃掉细节。",
        ],
      },
      {
        heading: "面料与剪裁的取舍",
        paragraphs: [
          "哑光面料因为反光更柔和，成为高频选择。剪裁上则出现了更多结构感明确的廓形，用线条而不是装饰来制造记忆点。",
          "配饰方面整体克制，金属色的点睛比堆叠更受欢迎。",
        ],
      },
    ],
    keyPoints: [
      "焦糖、砖红与米金等暖色成为红毯主流配色",
      "哑光面料因上镜柔和而高频出现",
      "配饰趋向克制，以金属色点睛代替堆叠",
    ],
  },
];

export const tickerItems: string[] = [
  "新剧定档盘点：古装都市正面交锋",
  "综艺现场互动片段登上话题榜",
  "音乐节首日返场成为最高讨论点",
  "短视频舞蹈挑战两周持续刷屏",
  "品牌联名限定包装开售当天售罄",
  "服化道幕后流程首次被完整梳理",
];

export const hotRanking: { id: string; title: string; heat: number }[] = [
  { id: "p1", title: "新剧定档盘点：古装与都市正面交锋", heat: 9821 },
  { id: "p2", title: "综艺现场互动引热议：效果与真实的边界", heat: 9120 },
  { id: "p4", title: "短视频热门挑战复盘：一支舞蹈为何火遍全网", heat: 7980 },
  { id: "p3", title: "音乐节首日回顾：多组乐队带来惊喜返场", heat: 8340 },
  { id: "p5", title: "品牌联名再出圈：限定包装上线即售罄", heat: 7210 },
];

export const editorPicks: string[] = ["p6", "p7", "p10"];